import { APIGet, APIPost, APIPut, APIUrl, AdminType, createFormData } from "api/protocols";
import { CancelToken } from "axios";

import type {
  IArchiveEmailBuilderMediaParams,
  IEmailBuilderMediaItem,
  IEmailCampaignDesignVersion,
  IEmailCampaignDraft,
  IEmailCampaignDraftExport,
  IEmailCampaignDraftPreview,
  IEmailCampaignDraftTest,
  IPostEmailBuilderMediaParams,
  IPostEmailCampaignDesignPublishParams,
  IPostEmailCampaignDraftFromTemplateParams,
  IPostEmailCampaignDraftTestParams,
  IPutEmailCampaignDraftParams,
  IRestoreEmailBuilderMediaParams,
} from "./emailBuilderTypes";

export type {
  IArchiveEmailBuilderMediaParams,
  IEmailBuilderMediaItem,
  IEmailBuilderReferencedMediaEntry,
  IEmailCampaignDesignStatus,
  IEmailCampaignDesignVersion,
  IEmailCampaignDraft,
  IEmailCampaignDraftExport,
  IEmailCampaignDraftPreview,
  IEmailCampaignDraftTest,
  IPostEmailBuilderMediaParams,
  IPostEmailCampaignDesignPublishParams,
  IPostEmailCampaignDraftFromTemplateParams,
  IPostEmailCampaignDraftTestParams,
  IPutEmailCampaignDraftParams,
  IRestoreEmailBuilderMediaParams,
  TEmailBuilderReferencedMediaMap,
} from "./emailBuilderTypes";

export async function GetEmailCampaignDraft(campaignId: number, useGlobalLoader: boolean = false) {
  return await APIGet<IEmailCampaignDraft>(APIUrl(AdminType.FacilityAdmin, "/marketing/email-campaigns/draft"), { id: campaignId }, useGlobalLoader);
}

export async function PostEmailCampaignDraft(campaignId: number, useGlobalLoader: boolean = false) {
  return await APIPost<{ data: IEmailCampaignDraft }>(APIUrl(AdminType.FacilityAdmin, "/marketing/email-campaigns/draft"), { id: campaignId }, useGlobalLoader);
}

export async function PostEmailCampaignDraftFromTemplate(
  campaignId: number,
  params: IPostEmailCampaignDraftFromTemplateParams,
  useGlobalLoader: boolean = false,
) {
  return await APIPost<{ data: IEmailCampaignDraft }>(
    APIUrl(AdminType.FacilityAdmin, "/marketing/email-campaigns/draft/from-template"),
    { id: campaignId, ...params },
    useGlobalLoader,
  );
}

export async function PostEmailCampaignDraftReopen(campaignId: number, useGlobalLoader: boolean = false) {
  return await APIPost<{ data: IEmailCampaignDraft }>(
    APIUrl(AdminType.FacilityAdmin, "/marketing/email-campaigns/draft/reopen"),
    { id: campaignId },
    useGlobalLoader,
  );
}

export async function PostEmailCampaignDraftDiscard(campaignId: number, useGlobalLoader: boolean = false) {
  return await APIPost<{ data: IEmailCampaignDraft }>(
    APIUrl(AdminType.FacilityAdmin, "/marketing/email-campaigns/draft/discard"),
    { id: campaignId },
    useGlobalLoader,
  );
}

export async function PostEmailCampaignDesignPublish(
  campaignId: number,
  params: IPostEmailCampaignDesignPublishParams,
  useGlobalLoader: boolean = false,
) {
  return await APIPost<{ data: IEmailCampaignDesignVersion }>(
    APIUrl(AdminType.FacilityAdmin, "/marketing/email-campaigns/design/publish"),
    { id: campaignId, ...params },
    useGlobalLoader,
  );
}

export async function PutEmailCampaignDraft(
  campaignId: number,
  params: IPutEmailCampaignDraftParams,
  useGlobalLoader: boolean = false,
  cancelToken?: CancelToken,
) {
  return await APIPut<IEmailCampaignDraft>(
    APIUrl(AdminType.FacilityAdmin, "/marketing/email-campaigns/draft"),
    { id: campaignId, ...params },
    useGlobalLoader,
    cancelToken,
  );
}

export async function PostEmailCampaignDraftPreview(campaignId: number, useGlobalLoader: boolean = false) {
  return await APIPost<{ data: IEmailCampaignDraftPreview }>(
    APIUrl(AdminType.FacilityAdmin, "/marketing/email-campaigns/draft/preview"),
    { id: campaignId },
    useGlobalLoader,
  );
}

export async function PostEmailCampaignDraftTest(
  campaignId: number,
  params: IPostEmailCampaignDraftTestParams,
  useGlobalLoader: boolean = false,
) {
  return await APIPost<{ data: IEmailCampaignDraftTest }>(
    APIUrl(AdminType.FacilityAdmin, "/marketing/email-campaigns/draft/test"),
    { id: campaignId, ...params },
    useGlobalLoader,
  );
}

export async function PostEmailCampaignDraftExport(campaignId: number, useGlobalLoader: boolean = false) {
  return await APIPost<{ data: IEmailCampaignDraftExport }>(
    APIUrl(AdminType.FacilityAdmin, "/marketing/email-campaigns/draft/export"),
    { id: campaignId },
    useGlobalLoader,
  );
}

export async function GetEmailBuilderMedia(useGlobalLoader: boolean = false, cancelToken?: CancelToken) {
  return await APIGet<IEmailBuilderMediaItem[]>(APIUrl(AdminType.FacilityAdmin, "/marketing/email-builder/media"), {}, useGlobalLoader, cancelToken);
}

export async function GetEmailBuilderStockMedia(useGlobalLoader: boolean = false, cancelToken?: CancelToken) {
  return await APIGet<IEmailBuilderMediaItem[]>(APIUrl(AdminType.FacilityAdmin, "/marketing/email-builder/media/stock"), {}, useGlobalLoader, cancelToken);
}

export async function PostEmailBuilderMedia(
  params: IPostEmailBuilderMediaParams,
  useGlobalLoader: boolean = false,
) {
  return await APIPost<{ data: IEmailBuilderMediaItem }>(
    APIUrl(AdminType.FacilityAdmin, "/marketing/email-builder/media"),
    createFormData(params),
    useGlobalLoader,
  );
}

export async function ArchiveEmailBuilderMedia(
  params: IArchiveEmailBuilderMediaParams,
  useGlobalLoader: boolean = false,
) {
  return await APIPut<IEmailBuilderMediaItem>(APIUrl(AdminType.FacilityAdmin, "/marketing/email-builder/media/archive"), params, useGlobalLoader);
}

export async function RestoreEmailBuilderMedia(
  params: IRestoreEmailBuilderMediaParams,
  useGlobalLoader: boolean = false,
) {
  return await APIPut<IEmailBuilderMediaItem>(APIUrl(AdminType.FacilityAdmin, "/marketing/email-builder/media/restore"), params, useGlobalLoader);
}
